<template>
  <div
    :class="[{ highLight: notice }, 'order-card max-h-60 rounded-1 border-1 border-gray-30 bg-gray-10 px-4 pb-4 pt-5 flex flex-col gap-3 relative']"
  >
    <div class="flex flex-row justify-between gap-3 pb-3 border-b-gray-40 border-b-1">
      <div class="flex flex-col justify-center gap-2">
        <div class="text-lg text-black-50 line-clamp-2 overflow-ellipsis">{{ order.product_name }}</div>
        <div>
          <span :class="['px-2 py-1 rounded-1 text-xs text-gray-10', statusDisplayData.class]">{{ statusDisplayData.text }}</span>
        </div>
      </div>
      <div class="bg-gray-20 rounded-1 w-20 h-20 aspect-1">
        <nd-img v-show="order.cover_image" class="block aspect-1 object-cover" :src="`fn:${order.cover_image}`" :alt="order.product_name" />
      </div>
    </div>
    <div class="flex flex-row gap-2 justify-between items-center">
      <div>
        <div class="text-xs text-gray-80">{{ format(startDate, 'yyyy') }}</div>
        <div class="text-xl text-black-50 font-700">
          {{ format(startDate, 'MM/dd') }}<span class="ml-3">{{ format(startDate, 'HH:mm') }}</span>
        </div>
        <div class="text-xs text-gray-80">{{ format(startDate, 'EEE') }}</div>
      </div>
      <div class="w-6 h-6 min-w-6 flex-grow-4 flex justify-center">
        <svg-icon name="tilde" color="black-50" />
      </div>
      <div class="max-w-1/3">
        <div v-if="!showEndTimeTextDirectly">
          <div class="text-xs text-gray-80">{{ format(endDate, 'yyyy') }}</div>
          <div class="text-xl text-black-50 font-700">
            {{ format(endDate, 'MM/dd') }}<span class="ml-3">{{ format(endDate, 'HH:mm') }}</span>
          </div>
          <div class="text-xs text-gray-80">{{ format(endDate, 'EEE') }}</div>
        </div>
        <div v-else>
          <div class="text-xl text-black-50 font-700 line-clamp-2">
            {{ props.order.end_time_text }}
          </div>
        </div>
      </div>
    </div>
    <div v-if="notice" class="absolute -right-0.5 -top-0.5 px-2 py-0.5 bg-yellow-50 text-gray-10 text-xs rounded-1 flex flex-row items-center">
      {{ notice.text }}
      <svg-icon v-if="notice.icon" :name="notice.icon" color="gray-10" class="w-3 h-3 ml-1" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { parseISO } from 'date-fns'
import { OrderListDisplayStatus } from '@/types/order'
import { SattendeeDisplayStatus as ApiSattendeeDisplayStatus } from '@/types/api'
import type { NicedayOrderListData } from '@/types/api'

const { t } = useI18n()
const { format } = await useI18nDateFns()

const statusMap = computed(() => ({
  [OrderListDisplayStatus.GENERAL_SUCCESS]: {
    text: t('all_booking_status_done'),
    class: 'bg-yellow-50',
  },
  [OrderListDisplayStatus.REDEEMED]: {
    text: t('all_booking_status_redeemed'),
    class: 'bg-green-60',
  },
  [OrderListDisplayStatus.CERTIFICATION_REDEEMED]: {
    text: t('all_booking_status_voucher_can_download'),
    class: 'bg-green-60',
  },
  [OrderListDisplayStatus.REVIEWED]: {
    text: t('all_booking_status_reviewed'),
    class: 'bg-green-60',
  },
  [OrderListDisplayStatus.GENERAL_CANCELLED]: {
    text: t('all_booking_status_cancelled'),
    class: 'bg-gray-80',
  },
}))

const props = defineProps<{
  order: Partial<NicedayOrderListData>
}>()

const startDate = computed(() => parseISO(props.order.booking_time ?? ''))
const endDate = computed(() => parseISO(props.order.end_time_text ?? ''))
const showEndTimeTextDirectly = computed(() => {
  const target = new Date(props.order.end_time_text ?? '')
  return !(target instanceof Date) || isNaN(target.valueOf())
})

const notice = computed(() => {
  if (
    props.order.display_status === OrderListDisplayStatus.GENERAL_SUCCESS &&
    props.order.attendee_status === ApiSattendeeDisplayStatus.DisplayStatusUnfinished
  ) {
    return { text: t('booking_card_status_not_filled_attendee'), icon: 'info' }
  }
  if (
    (props.order.display_status === OrderListDisplayStatus.REDEEMED ||
      props.order.display_status === OrderListDisplayStatus.CERTIFICATION_REDEEMED) &&
    !props.order.reviewed
  ) {
    if (props.order.review_bonus) {
      return { text: t('booking_card_status_funcion_feedback', [props.order.review_bonus]), icon: 'fun-coin-circle' }
    }
    return { text: t('booking_card_status_give_review'), icon: '' }
  }

  return null
})

const statusDisplayData = computed(() => statusMap.value[props.order.display_status as OrderListDisplayStatus])
</script>

<style lang="scss" scoped>
.highLight {
  @apply border-2 border-yellow-50;
}
</style>
